import type { CapturedEventFromCallback, EventsExtractedFromBatch } from "./types.js";

export function getParsedQueryString(href: string) {
  const url = new URL(href);
  return Object.fromEntries(url.searchParams);
}

// Extracts ep.* (string) and epn.* (numeric) event params from a GA4 param map.
function parseGa4Properties(params: Record<string, string>): Record<string, unknown> | undefined {
  const props: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (key.startsWith('ep.')) {
      props[key.slice(3)] = value;
    } else if (key.startsWith('epn.')) {
      const num = Number(value);
      props[key.slice(4)] = Number.isNaN(num) ? value : num;
    }
  }
  return Object.keys(props).length > 0 ? props : undefined;
}

export function extractEventsFromBatch(
  event: CapturedEventFromCallback,
): EventsExtractedFromBatch {
  try {
    const commonProps = {
      href: event.href,
      method: event.method,
      transport: event.transport,
      provider: event.provider,
      sentAt: event.sentAt,
    };
    switch (event.provider) {
      case 'googleanalytics': {
        const parsedQS = getParsedQueryString(event.endpoint);
        const isSingleEvent = Object.hasOwn(parsedQS, 'en');
        if (isSingleEvent) {
          return [
            {
              ...commonProps,
              eventName: parsedQS.en ?? 'unknown',
              body: null,
              properties: parseGa4Properties(parsedQS),
            },
          ];
        } else {
          const eventBodiesInBatch = event.payload?.split(/\s/).filter(Boolean);
          if (!eventBodiesInBatch) {
            return [];
          }

          return eventBodiesInBatch.map(ev => {
            const params = Object.fromEntries(new URLSearchParams(ev));
            return {
              ...commonProps,
              eventName: params.en ?? 'unknown',
              body: ev,
              properties: parseGa4Properties(params),
            };
          });
        }
      }
      case 'mixpanel': {
        if (!event.payload) return [];
        const eventsInBatch = JSON.parse(
          new URLSearchParams(event.payload).get('data') || '[]',
        ) as Record<string, unknown>[];
        return eventsInBatch.map(ev => ({
          ...commonProps,
          eventName: (ev.event as string) || 'unknown',
          body: JSON.stringify(ev),
          properties: ev.properties as Record<string, unknown> | undefined,
        }));
      }
      case 'segment': {
        const parsedPayload =
          event.payload &&
          (JSON.parse(event.payload) as Record<string, unknown>);
        const isBatch =
          typeof parsedPayload === 'object' &&
          parsedPayload !== null &&
          Object.hasOwn(parsedPayload, 'batch');
        if (isBatch) {
          const batchedEvents = parsedPayload.batch as Record<
            string,
            unknown
          >[];
          return batchedEvents.map(ev => ({
            ...commonProps,
            eventName:
              ev.type === 'track' ? (ev.event as string) : (ev.type as string),
            body: JSON.stringify(ev),
            properties: ev.properties as Record<string, unknown> | undefined,
          }));
        } else if (parsedPayload) {
          return [
            {
              ...commonProps,
              body: event.payload || null,
              eventName:
                parsedPayload.type === 'track'
                  ? (parsedPayload.event as string)
                  : (parsedPayload.type as string),
              properties: parsedPayload.properties as Record<string, unknown> | undefined,
            },
          ];
        } else {
          throw Error('something wrong with segment');
        }
      }
      default:
        return [];
    }
  } catch (error) {
    console.error(
      'something wrong in extraction',
      event.endpoint,
      event.provider,
      event.payload,
      error,
    );
    return [];
  }
}
